import React, { useState, useEffect, useRef } from "react";
import { createGame, fetchGames, searchIgdbGames } from "../lib/api";

/**
 * Game picker with two modes:
 *  1. Default — dropdown of existing games (the master catalog).
 *  2. "Add new" — debounced IGDB search. Picking a result calls
 *     `POST /api/games` with the full metadata payload so cover art,
 *     platforms, and release date are persisted globally and league
 *     cards can render rich tiles afterwards.
 *
 * Props:
 *  - value: current game name
 *  - onChange: (gameName, gameDoc) => void
 *  - games: list from /api/games  [{name, slug, category, icon, cover_url?, platforms?}]
 *  - onGamesUpdated: callback to refresh games list after add
 *  - includeAllOption: if true, prepends "All Games" choice (value "")
 *  - onToast: feedback handler
 *  - dataTestid
 */
export function GameSelect({
  value, onChange, games, onGamesUpdated, includeAllOption = false, onToast, dataTestid,
}) {
  const [adding, setAdding] = useState(false);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [busy, setBusy] = useState(false);
  const [igdbAvailable, setIgdbAvailable] = useState(true);
  const debounceRef = useRef(null);

  // Debounced IGDB search.
  useEffect(() => {
    if (!adding) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    const q = query.trim();
    if (q.length < 2) {
      setResults([]);
      setSearching(false);
      return;
    }
    setSearching(true);
    debounceRef.current = setTimeout(async () => {
      try {
        const data = await searchIgdbGames(q, 8);
        setIgdbAvailable(data.configured !== false);
        setResults(data.results || []);
      } catch {
        setResults([]);
      } finally {
        setSearching(false);
      }
    }, 280);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
  }, [query, adding]);

  const handleChange = (e) => {
    const v = e.target.value;
    if (v === "__add__") {
      setAdding(true);
    } else {
      const doc = games.find((g) => g.name === v);
      onChange(v, doc);
    }
  };

  const pickResult = async (item) => {
    if (busy) return;
    setBusy(true);
    try {
      const created = await createGame({
        name: item.name,
        category: "Other",
        igdb_id: item.igdb_id,
        cover_url: item.cover_url,
        platforms: item.platforms,
        release_date: item.release_date,
      });
      if (onGamesUpdated) {
        const fresh = await fetchGames();
        onGamesUpdated(fresh);
      }
      onChange(created.name, created);
      onToast && onToast(`Added "${created.name}" to your league.`);
      setAdding(false);
      setQuery("");
      setResults([]);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Could not add game.";
      onToast && onToast(msg);
    } finally {
      setBusy(false);
    }
  };

  const addManual = async () => {
    const name = query.trim();
    if (name.length < 2) {
      onToast && onToast("Game name too short.");
      return;
    }
    setBusy(true);
    try {
      const created = await createGame({ name, category: "Other" });
      if (onGamesUpdated) {
        const fresh = await fetchGames();
        onGamesUpdated(fresh);
      }
      onChange(created.name, created);
      onToast && onToast(`Added "${created.name}".`);
      setAdding(false);
      setQuery("");
      setResults([]);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Could not add game.";
      onToast && onToast(msg);
    } finally {
      setBusy(false);
    }
  };

  if (adding) {
    return (
      <div className="space-y-2" data-testid={`${dataTestid}-adding`}>
        <input
          autoFocus
          placeholder="Search games… (e.g. Valorant, Apex, Rocket League)"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          data-testid={`${dataTestid}-search-input`}
          className="w-full bg-black/40 border border-[#14b8a6]/50 rounded-2xl px-4 py-3 text-white"
        />
        <div
          data-testid={`${dataTestid}-search-results`}
          className="max-h-64 overflow-y-auto rounded-2xl border border-[#14b8a6]/20 bg-black/40 divide-y divide-white/5"
        >
          {searching && (
            <div className="px-4 py-3 text-xs text-[#14b8a6]/80 tracking-widest">
              SEARCHING IGDB…
            </div>
          )}
          {!searching && query.trim().length >= 2 && results.length === 0 && (
            <div className="px-4 py-3 text-xs text-white/60">
              {igdbAvailable
                ? "No matches. You can still add it manually below."
                : "Game library is offline. Add manually below."}
            </div>
          )}
          {!searching && results.map((item) => (
            <button
              type="button"
              key={item.igdb_id}
              onClick={() => pickResult(item)}
              disabled={busy}
              data-testid={`${dataTestid}-result-${item.igdb_id}`}
              className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-[#14b8a6]/10 text-left transition disabled:opacity-50"
            >
              {item.cover_url ? (
                <img
                  src={item.cover_url}
                  alt=""
                  className="w-9 h-12 object-cover rounded-md bg-black/40 flex-shrink-0"
                  loading="lazy"
                />
              ) : (
                <div className="w-9 h-12 rounded-md bg-black/40 flex items-center justify-center flex-shrink-0">
                  <i className="fas fa-gamepad text-[#14b8a6]/60 text-sm" />
                </div>
              )}
              <div className="min-w-0 flex-1">
                <div className="text-sm font-semibold text-white truncate">
                  {item.name}
                  {item.release_date && (
                    <span className="ml-2 text-[10px] text-white/40 font-normal">
                      {item.release_date.slice(0, 4)}
                    </span>
                  )}
                </div>
                {item.platforms?.length > 0 && (
                  <div className="text-[10px] text-[#14b8a6]/80 truncate">
                    {item.platforms.slice(0, 4).join(" · ")}
                  </div>
                )}
              </div>
            </button>
          ))}
        </div>
        <div className="grid grid-cols-2 gap-2">
          <button
            type="button"
            onClick={() => { setAdding(false); setQuery(""); setResults([]); }}
            className="btn-ghost rounded-2xl py-2.5 text-sm"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={addManual}
            disabled={busy || query.trim().length < 2}
            data-testid={`${dataTestid}-save-manual`}
            className="btn-neon rounded-2xl py-2.5 text-sm disabled:opacity-40"
          >
            {busy ? "ADDING…" : "+ ADD MANUALLY"}
          </button>
        </div>
        <p className="text-[10px] text-[#a3a3ac]">
          Powered by IGDB. Pick a result to attach cover art &amp; platforms automatically.
        </p>
      </div>
    );
  }

  return (
    <select
      value={value}
      onChange={handleChange}
      data-testid={dataTestid}
      className="w-full bg-black/40 border border-[#14b8a6]/30 rounded-2xl px-4 py-3 text-white"
    >
      {includeAllOption && <option value="">ALL GAMES</option>}
      {games.map((g) => (
        <option key={g.slug} value={g.name}>{g.name}{g.category ? ` • ${g.category}` : ""}</option>
      ))}
      <option value="__add__">＋ SEARCH / ADD GAME…</option>
    </select>
  );
}

export default GameSelect;
